/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2021, Bulb Inc.  For more information, please email contact@hellobulb.com.
 *
 *                _
 *  _            | | _
 * | |         _ | || |
 * | |__   _  | || || |__
 * | '_ \ | | | || || '_ \
 * | (_) || |_| || || (_) |
 *  \___/  \___/ |_| \___/
 *
 */

/**
 * @module bulb-progress/bulb-upload/bulbuploadediting
 */

import { Plugin, Widget, toWidget } from 'ckeditor5';
import { BulbInsertUploadPlaceholderCommand } from './bulbuploadcommand';

/**
 * The bulb attached file editing, this will create the block using the converters.
 *
 * @extends Plugin
 */
export default class BulbUploadEditing extends Plugin {

  /**
   * @inheritDoc
   */
  public static get requires() {
    return [Widget] as const;
  }

  /**
   * @inheritDoc
   */
  public static get pluginName() {
    return 'BulbUploadEditing' as const;
  }

  public init(): void {
    this.defineSchema();
    this.defineConverters();

    this.editor.commands.add('bulbInsertUploadPlaceholder', new BulbInsertUploadPlaceholderCommand(this.editor));
  }

  private defineSchema(): void {
    const { schema } = this.editor.model;

    schema.register('bulbUploadPlaceholder', {
      isObject: true,
      allowWhere: '$block',
      allowAttributes: ['asset-type', 'asset-id', 'asset-name', 'asset-data', 'upload-succeeded'],
    });
  }

  private defineConverters(): void {
    const { conversion } = this.editor;

    conversion.for('upcast').elementToElement({
      view: {
        name: 'figure',
        classes: 'bulb-upload-placeholder',
      },
      model: (viewElement, { writer: modelWriter }) => modelWriter.createElement('bulbUploadPlaceholder', {
        'asset-type': viewElement.getAttribute('asset-type'),
        'asset-id': viewElement.getAttribute('asset-id'),
        'asset-name': viewElement.getAttribute('asset-name'),
        'upload-succeeded': viewElement.getAttribute('upload-succeeded'),
      }),
    });

    conversion.for('dataDowncast').elementToElement({
      model: 'bulbUploadPlaceholder',
      view: (modelElement, { writer: viewWriter }) => viewWriter.createEmptyElement('figure', {
        class: 'bulb-upload-placeholder',
        'asset-type': modelElement.getAttribute('asset-type'),
        'asset-id': modelElement.getAttribute('asset-id'),
        'asset-name': modelElement.getAttribute('asset-name'),
      }),
    });

    conversion.for('editingDowncast').elementToElement({
      model: 'bulbUploadPlaceholder',
      view: (modelElement, { writer: viewWriter }) => {
        const emptyElement = viewWriter.createEmptyElement('img');
        const figure = viewWriter.createContainerElement('figure', {
          class: 'bulb-upload-placeholder',
          'asset-type': modelElement.getAttribute('asset-type'),
          'asset-id': modelElement.getAttribute('asset-id'),
          'asset-name': modelElement.getAttribute('asset-name'),
        });

        viewWriter.insert(viewWriter.createPositionAt(figure, 0), emptyElement);
        viewWriter.setCustomProperty('bulbUploadPlaceholder', true, figure);

        return toWidget(figure, viewWriter, { label: 'Bulb Placeholder', hasSelectionHandle: true });
      },
    });
  }
}
